/**
 * RS Customizer — Spaced Alphabets
 * Visual preview renderer for spaced alphabets customization.
 * Letters are per-variant: GraphQL returns images keyed by variant title.
 * Renders letter images positioned along an SVG path with odd/even spacing.
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const METAOBJECT_TYPE = 'customizer_spaced_alphabets_set';
  const LIQUID_VIEW = 'metaobject-spaced-alphabets';

  const GRAPHQL_QUERY = `
    query getSpacedAlphabetsMetaobject($handle: MetaobjectHandleInput!) {
      metaobject(handle: $handle) {
        handle
        type
        fields {
          key
          value
          references(first: 250) {
            edges {
              node {
                ... on Metaobject {
                  fields {
                    key
                    value
                    reference {
                      ... on MediaImage {
                        image { url width height }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const BASELINE_WIDTH = 1110;
  const MIN_WIDTH = 300;
  const MAX_WIDTH = 1110;

  let _injectedSlideId = null;
  let _baseConfig = null;
  // Per-variant letter map: { 'Gold': { 'A': letterData, 'B': letterData, ... }, ... }
  // For products with only default variant: { 'Default Variant': { 'A': ..., ... } }
  let _letters = null;
  let _backgroundsData = null;
  let _currentVariantTitle = null;
  let _resizeTimer = null;

  // ─── Letter data lookup ─────────────────────────────────────────

  function _getLetterData(char) {
    if (!_letters || !_currentVariantTitle) return null;

    const variantKey = (_baseConfig && _baseConfig.has_only_default_variant)
      ? 'Default Variant'
      : _currentVariantTitle;

    const variantLetters = _letters[variantKey];
    if (!variantLetters) return null;

    const upperChar = char.toUpperCase();
    if (variantLetters[upperChar]) return variantLetters[upperChar];

    const keys = Object.keys(variantLetters);
    const found = keys.find(function (k) { return k.toLowerCase() === char.toLowerCase(); });
    return found ? variantLetters[found] : null;
  }

  // ─── DOM helpers ────────────────────────────────────────────────

  function _getMtoText() {
    const fields = document.querySelectorAll('[data-mto-field]');
    let text = '';
    fields.forEach(function (field) { text += field.value; });
    const trimmed = text.trim();
    return (_baseConfig && _baseConfig.letter_uppercase) ? trimmed.toUpperCase() : trimmed;
  }

  // ─── Data fetching ──────────────────────────────────────────────

  async function _fetchBaseConfig() {
    const response = await fetch(
      window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW
    );
    return response.json();
  }

  async function _fetchRawLetters() {
    if (!window.RSGraphQL) return null;
    try {
      return await window.RSGraphQL.executeQuery(GRAPHQL_QUERY, {
        handle: {
          handle: window.RSProduct.config.productHandle,
          type: METAOBJECT_TYPE,
        },
      });
    } catch (err) {
      console.error('[SpacedAlphabets] GraphQL fetch failed:', err);
      return null;
    }
  }

  // Called after _baseConfig is set so hasOnlyDefaultVariant + product_title are available.
  function _extractLetters(data) {
    if (!data || !data.metaobject) return {};

    const letters = {};
    const hasOnlyDefault = _baseConfig && _baseConfig.has_only_default_variant;

    // Pre-initialise variant buckets
    if (hasOnlyDefault) {
      letters['Default Variant'] = {};
    } else if (_baseConfig && _baseConfig.variant_names) {
      Object.values(_baseConfig.variant_names).forEach(function (fullName) {
        const variantTitle = fullName.split(' - ').slice(-1)[0];
        letters[variantTitle] = {};
      });
    }

    const lettersField = data.metaobject.fields.find(function (f) { return f.key === 'letters'; });
    if (!lettersField || !lettersField.references || !lettersField.references.edges) return letters;

    lettersField.references.edges.forEach(function (edge) {
      const node = edge.node;
      const entry = {};
      let letterChar = '';
      let letterName = '';

      node.fields.forEach(function (field) {
        switch (field.key) {
          case 'character':
            letterChar = field.value;
            entry.character = field.value;
            break;
          case 'name':
            letterName = field.value;
            entry.name = field.value;
            break;
          case 'rotation':
            entry.rotation = parseFloat(field.value) || 0;
            break;
          case 'max_width':
            entry.max_width = parseFloat(field.value) || 108;
            break;
          case 'max_height':
            entry.max_height = parseFloat(field.value) || 80;
            break;
          case 'image':
            if (field.reference && field.reference.image) {
              entry.image = field.reference.image.url;
              entry.image_width = field.reference.image.width || 543;
              entry.image_height = field.reference.image.height || 402;
            }
            break;
          default:
            break;
        }
      });

      if (!letterChar || !letterName || !entry.image) return;

      if (hasOnlyDefault) {
        const prefix = ((_baseConfig && _baseConfig.product_title) || '') + ' - ';
        if (letterName.startsWith(prefix)) {
          letters['Default Variant'][letterChar.toUpperCase()] = entry;
        }
      } else {
        const parts = letterName.split(' - ');
        if (parts.length >= 2) {
          const variantTitle = parts.slice(0, -1).join(' - ');
          if (letters[variantTitle]) {
            letters[variantTitle][letterChar.toUpperCase()] = entry;
          }
        }
      }
    });

    return letters;
  }

  // ─── Background management ──────────────────────────────────────

  function _getBackgroundForVariant(variantTitle) {
    if (!_backgroundsData || !variantTitle) return null;
    const key = variantTitle.toLowerCase();
    let match = null;
    Object.entries(_backgroundsData).forEach(function (pair) {
      if (pair[0].toLowerCase() === key || key.includes(pair[0].toLowerCase())) {
        match = pair[1];
      }
    });
    if (!match) {
      const keys = Object.keys(_backgroundsData);
      if (keys.length > 0) match = _backgroundsData[keys[0]];
    }
    return match;
  }

  // ─── Canvas management ──────────────────────────────────────────

  function _getCanvas() {
    if (!_injectedSlideId) return null;
    const slide = document.querySelector('[data-rs-injected="' + _injectedSlideId + '"]');
    if (!slide) return null;
    const customizerView = slide.querySelector('.customizer-view');
    if (!customizerView) return null;
    let canvas = customizerView.querySelector('.rs-customizer-canvas');
    if (!canvas) canvas = _buildCanvas(customizerView);
    return canvas;
  }

  function _buildCanvas(customizerView) {
    const canvas = document.createElement('div');
    canvas.className = 'rs-customizer-canvas';

    // SVG container — populated on every render call (path may vary per render).
    const svgContainer = document.createElement('div');
    svgContainer.className = 'rs-customizer-path-svg';
    svgContainer.setAttribute('aria-hidden', 'true');
    canvas.appendChild(svgContainer);

    const letterContainer = document.createElement('div');
    letterContainer.className = 'rs-customizer-letters';
    canvas.appendChild(letterContainer);

    customizerView.appendChild(canvas);
    return canvas;
  }

  function _getLetterContainer(canvas) {
    return canvas ? canvas.querySelector('.rs-customizer-letters') : null;
  }

  function _getSvgContainer(canvas) {
    return canvas ? canvas.querySelector('.rs-customizer-path-svg') : null;
  }

  // ─── Letter rendering ───────────────────────────────────────────

  function _renderLetters() {
    const text = _getMtoText();
    const canvas = _getCanvas();
    if (!canvas) return;

    const letterContainer = _getLetterContainer(canvas);
    if (!letterContainer) return;

    const svgContainer = _getSvgContainer(canvas);

    // Re-inject SVG guide content on every render.
    if (svgContainer && _baseConfig && _baseConfig.svg_guide_content) {
      svgContainer.innerHTML = _baseConfig.svg_guide_content;
    }

    letterContainer.querySelectorAll('[data-rs-customizer-item]').forEach(function (el) {
      el.remove();
    });

    if (!text || !_letters) return;

    const chars = text.split('').filter(function (ch) {
      const ld = _getLetterData(ch);
      return ld && ld.image;
    });

    if (!chars.length) return;

    const pathEl = svgContainer ? svgContainer.querySelector('path') : null;
    if (!pathEl) return;

    const positions = _calculatePositions(chars, pathEl, letterContainer);
    _mountLetters(chars, positions, letterContainer);
  }

  function _calculatePositions(chars, pathEl, letterContainer) {
    const pathLength = pathEl.getTotalLength();
    if (!Number.isFinite(pathLength) || pathLength <= 0) return [];

    const containerRect = letterContainer.getBoundingClientRect();
    const viewBoxWidth = (_baseConfig && _baseConfig.svg_view_box_width) || BASELINE_WIDTH;
    const viewBoxHeight = (_baseConfig && _baseConfig.svg_view_box_height) || 880;
    const scaleX = containerRect.width / viewBoxWidth;
    const scaleY = containerRect.height / viewBoxHeight;

    // NOTE: spacing is in display pixels and added directly to path position
    // (no /scaleX conversion) — matching old implementation behaviour.
    let totalSpacing = 0;
    for (let i = 0; i < chars.length - 1; i++) {
      totalSpacing += _getLetterSpacing(chars[i], chars[i + 1], chars.length, containerRect.width);
    }

    let startPos = (pathLength - totalSpacing) / 2;
    if (startPos < 0) startPos = 0;
    if (startPos + totalSpacing > pathLength) startPos = Math.max(0, pathLength - totalSpacing);

    const positions = [];
    let currentPos = startPos;
    const yOffset = (_baseConfig && _baseConfig.y_offset) || 0;

    for (let i = 0; i < chars.length; i++) {
      const clamped = Math.min(Math.max(currentPos, 0), pathLength);
      const point = pathEl.getPointAtLength(clamped);
      const angle = _getAngleAt(clamped, pathEl, pathLength);
      const ld = _getLetterData(chars[i]);
      const rotation = ld ? (ld.rotation || 0) : 0;

      // Perpendicular y_offset (matching old implementation).
      const angleRad = (angle * Math.PI) / 180;
      const offsetX = -Math.sin(angleRad) * yOffset;
      const offsetY = Math.cos(angleRad) * yOffset;

      positions.push({
        x: (point.x + offsetX) * scaleX,
        y: (point.y + offsetY) * scaleY,
        rotation: angle + rotation,
        char: chars[i],
      });

      if (i < chars.length - 1) {
        const spacing = _getLetterSpacing(chars[i], chars[i + 1], chars.length, containerRect.width);
        if (Number.isFinite(spacing) && spacing > 0) currentPos += spacing;
      }
    }

    return positions;
  }

  function _mountLetters(chars, positions, letterContainer) {
    positions.forEach(function (posData, i) {
      const ld = _getLetterData(posData.char);
      if (!ld || !ld.image) return;

      const wrapper = document.createElement('div');
      wrapper.setAttribute('data-rs-customizer-item', String(i));
      wrapper.className = 'rs-customizer-item';

      const img = document.createElement('img');
      img.src = ld.image;
      img.alt = posData.char;
      img.className = 'rs-customizer-letter-img';

      const containerWidth = _getContainerWidth();
      const dims = _getLetterDimensions(posData.char, containerWidth);
      img.style.width = dims.width + 'px';
      img.style.height = dims.height + 'px';
      img.style.filter = 'none';

      // Responsive Y offset compensates for whitespace in letter images.
      const yOffsetAdjust = _getResponsiveYOffset(containerWidth);
      img.style.position = 'relative';
      img.style.top = yOffsetAdjust + 'px';
      img.style.transformOrigin = 'center top';
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.3s';
      img.style.pointerEvents = 'none';

      const opacity = String((_baseConfig && _baseConfig.letter_opacity != null)
        ? _baseConfig.letter_opacity : 1);
      img.addEventListener('load', function () { img.style.opacity = opacity; });
      img.addEventListener('error', function () { img.style.opacity = opacity; });

      wrapper.style.left = posData.x + 'px';
      wrapper.style.top = posData.y + 'px';
      wrapper.style.transform = 'translate(-50%, -50%) rotate(' + posData.rotation + 'deg)';

      wrapper.appendChild(img);
      letterContainer.appendChild(wrapper);
    });
  }

  // ─── Sizing helpers ─────────────────────────────────────────────

  function _getContainerWidth() {
    if (!_injectedSlideId) return BASELINE_WIDTH;
    const slide = document.querySelector('[data-rs-injected="' + _injectedSlideId + '"]');
    return slide ? slide.getBoundingClientRect().width : BASELINE_WIDTH;
  }

  function _getScaleFactor(containerWidth) {
    let scale;
    if (containerWidth < MIN_WIDTH) {
      scale = 0.5;
    } else if (containerWidth > MAX_WIDTH) {
      scale = 0.85;
    } else {
      const progress = (containerWidth - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH);
      scale = 0.5 + progress * 0.35;
    }
    if (window.innerWidth <= 1024) scale *= 0.8;
    return scale;
  }

  function _getResponsiveHeight(char, containerWidth) {
    const ld = _getLetterData(char);
    let baseHeight;
    if (ld && ld.max_width && ld.max_height) {
      baseHeight = ld.max_height;
    } else if (ld && ld.max_height) {
      baseHeight = ld.max_height;
    } else {
      baseHeight = 80;
    }
    const letterScale = (_baseConfig && _baseConfig.letter_scale) || 1.0;
    return baseHeight * _getScaleFactor(containerWidth) * letterScale;
  }

  function _getLetterDimensions(char, containerWidth) {
    if (!containerWidth) containerWidth = _getContainerWidth();
    const ld = _getLetterData(char);
    const letterScale = (_baseConfig && _baseConfig.letter_scale) || 1.0;
    const height = _getResponsiveHeight(char, containerWidth);

    let width;

    if (ld && ld.max_width && ld.max_height) {
      const aspectRatio = ld.max_width / ld.max_height;
      width = height * aspectRatio;
      if (width > ld.max_width * letterScale) {
        width = ld.max_width * letterScale;
        const h = width / aspectRatio;
        return {
          width: Number.isFinite(width) && width > 0 ? width : 60,
          height: Number.isFinite(h) && h > 0 ? h : 60,
        };
      }
      if (height > ld.max_height * letterScale) {
        const h = ld.max_height * letterScale;
        width = h * aspectRatio;
        return {
          width: Number.isFinite(width) && width > 0 ? width : 60,
          height: Number.isFinite(h) && h > 0 ? h : 60,
        };
      }
    } else if (ld && ld.image_width && ld.image_height) {
      width = height * (ld.image_width / ld.image_height);
      if (ld.max_width && width > ld.max_width * letterScale) {
        width = ld.max_width * letterScale;
      }
    } else {
      const baseWidth = (_baseConfig && _baseConfig.letter_width) || 72;
      width = baseWidth * _getScaleFactor(containerWidth) * letterScale;
    }

    return {
      width: Number.isFinite(width) && width > 0 ? width : 60,
      height: Number.isFinite(height) && height > 0 ? height : 60,
    };
  }

  function _getResponsiveYOffset(containerWidth) {
    if (!containerWidth) containerWidth = _getContainerWidth();
    const baseYOffset = 20;
    let scaleFactor;
    if (containerWidth < MIN_WIDTH) {
      scaleFactor = 0.6;
    } else if (containerWidth > MAX_WIDTH) {
      scaleFactor = 1.0;
    } else {
      const progress = (containerWidth - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH);
      scaleFactor = 0.6 + progress * 0.4;
    }
    let yOffset = baseYOffset * scaleFactor;
    if (window.innerWidth <= 1024) yOffset *= 0.8;
    return yOffset;
  }

  // Returns spacing in display pixels — added directly to path position (no /scaleX).
  function _getLetterSpacing(currentChar, nextChar, totalCount, containerWidth) {
    const isEven = totalCount % 2 === 0;
    let baseSpacing = isEven
      ? ((_baseConfig && _baseConfig.even_letter_spacing) || 100)
      : ((_baseConfig && _baseConfig.odd_letter_spacing) || 90);

    if (window.innerWidth <= 1024) baseSpacing *= 1.5;

    const currentWidth = _getLetterDimensions(currentChar, containerWidth).width;
    const nextWidth = _getLetterDimensions(nextChar, containerWidth).width;

    return currentWidth / 2 + nextWidth / 2 + baseSpacing;
  }

  function _getAngleAt(position, pathEl, pathLength) {
    // Larger delta (0.005) for better accuracy at curved ends.
    const delta = Math.max(2, pathLength * 0.005);
    const p1 = pathEl.getPointAtLength(Math.max(0, position - delta));
    const p2 = pathEl.getPointAtLength(Math.min(pathLength, position + delta));
    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);
    return Number.isFinite(angle) ? angle : 0;
  }

  // ─── Event handlers ─────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    _renderLetters();
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;
    const variant = event.data.variant;
    if (!variant || !variant.title) return;

    _currentVariantTitle = variant.title;

    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    const backgroundUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
      if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () {
      const text = _getMtoText();
      if (text) _renderLetters();
    }, 250);
  }

  // ─── Initialization ─────────────────────────────────────────────

  async function _init() {
    if (!window.RSProduct.config.productHandle) return;

    // Fetch base config first — _extractLetters needs has_only_default_variant + product_title.
    let baseConfig;
    try {
      baseConfig = await _fetchBaseConfig();
    } catch (err) {
      console.error('[SpacedAlphabets] Base config fetch failed:', err);
      return;
    }

    _baseConfig = baseConfig;
    _backgroundsData = baseConfig.backgrounds || {};

    // Now fetch letters (GraphQL) in parallel with nothing else pending.
    let rawLettersData;
    try {
      rawLettersData = await _fetchRawLetters();
    } catch (err) {
      console.error('[SpacedAlphabets] Letters fetch failed:', err);
    }

    _letters = rawLettersData ? _extractLetters(rawLettersData) : {};

    const initialVariantId = String(window.RSProduct.config.initialVariantId || '');
    const variantNames = baseConfig.variant_names || {};
    const fullName = variantNames[initialVariantId] || '';
    const parts = fullName.split(' - ');
    _currentVariantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;

    const backgroundUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    document.addEventListener('input', _handleInput);
    document.addEventListener('focusin', _handleInputFocus);
    window.addEventListener('resize', _onResize);

    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, _handleVariantChange);
    }

    const existingText = _getMtoText();
    if (existingText) _renderLetters();
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.RSProduct) return;
    _init();
  });
})();
